import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { FileDown, Sparkles, FileText, ChevronDown, ChevronUp, Loader2, ExternalLink } from 'lucide-react';

const mdComponents = {
  h1: ({ children }) => (
    <h1 className="text-xl font-black text-neutral-950 mt-6 mb-3 first:mt-0 tracking-tight font-heading">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-lg font-bold text-neutral-900 mt-5 mb-2 pb-1 border-b border-neutral-200">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-bold text-neutral-800 mt-4 mb-2">{children}</h3>
  ),
  p: ({ children }) => <p className="text-sm text-neutral-600 leading-relaxed mb-3 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 mb-3 text-sm text-neutral-600 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-3 text-sm text-neutral-600 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-neutral-800">{children}</strong>,
  em: ({ children }) => <em className="italic text-neutral-500">{children}</em>,
  hr: () => <hr className="my-5 border-neutral-200" />,
  a: ({ href, children }) => (
    <a href={href} className="text-green-600 font-medium underline underline-offset-2 hover:text-green-700" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

function credentialsForReportUrl(href) {
  if (!href) return 'omit';
  return href.includes('/analyses/public/') ? 'omit' : 'include';
}

/**
 * Synthèse Markdown (digest OpenAI) : affichage + téléchargement.
 * Si le JSON ne contient pas markdown_report, charge le fichier depuis reportHref.
 */
export default function ContractDigestCard({
  markdown: markdownProp = '',
  reportHref,
  className = '',
  tone = 'neutral',
}) {
  const [showMd, setShowMd] = useState(true);
  const [fetchedMd, setFetchedMd] = useState('');
  const [loadState, setLoadState] = useState(() => {
    const p = markdownProp && String(markdownProp).trim();
    if (p) return 'ok';
    return reportHref ? 'loading' : 'idle';
  });
  const [retryKey, setRetryKey] = useState(0);

  const propMd = markdownProp && String(markdownProp).trim();
  const cred = credentialsForReportUrl(reportHref);

  useEffect(() => {
    if (!reportHref) {
      setFetchedMd('');
      setLoadState(propMd ? 'ok' : 'idle');
      return;
    }
    if (propMd) {
      setFetchedMd('');
      setLoadState('ok');
      return;
    }
    let cancelled = false;
    setLoadState('loading');
    fetch(reportHref, { credentials: cred })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then((t) => {
        if (cancelled) return;
        setFetchedMd((t && t.trim()) || '');
        setLoadState('ok');
      })
      .catch(() => {
        if (cancelled) return;
        setFetchedMd('');
        setLoadState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [reportHref, propMd, cred, retryKey]);

  const markdown = propMd || fetchedMd.trim();
  const hasBody = Boolean(markdown);

  if (!reportHref && !propMd) return null;

  const border =
    tone === 'green'
      ? 'border-green-200 bg-white'
      : 'border-neutral-200 bg-white';

  const btnOutline =
    tone === 'green'
      ? 'border-green-300 text-green-800 hover:bg-green-50'
      : 'border-neutral-300 text-neutral-800 hover:bg-neutral-50';

  return (
    <div
      className={`border p-5 sm:p-6 ${border} ${className}`}
      data-testid="contract-digest-card"
    >
      <div className="flex flex-col gap-4 mb-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className={tone === 'green' ? 'text-green-600' : 'text-neutral-400'} />
            <p className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
              Synthèse contrat (IA)
            </p>
          </div>
        </div>

        {/* Boutons toujours visibles si URL dispo */}
        {reportHref ? (
          <div className="flex flex-col sm:flex-row flex-wrap gap-2">
            <a
              href={reportHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-green inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold"
              data-testid="contract-digest-open-md-btn"
            >
              <ExternalLink size={16} />
              Ouvrir le rapport (.md)
            </a>
            <a
              href={reportHref}
              download="synthese-contrat.md"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-green-700 border-2 border-green-600 bg-white hover:bg-green-50 transition-colors"
              data-testid="contract-digest-download-link"
            >
              <FileDown size={16} />
              Télécharger synthese-contrat.md
            </a>
            {loadState === 'error' ? (
              <button
                type="button"
                onClick={() => setRetryKey((k) => k + 1)}
                className={`inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold border bg-white ${btnOutline}`}
                data-testid="contract-digest-retry-btn"
              >
                Réessayer le chargement
              </button>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {hasBody ? (
            <button
              type="button"
              onClick={() => setShowMd((v) => !v)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold border bg-white transition-colors rounded-none ${btnOutline}`}
              data-testid="contract-digest-toggle-md-btn"
              aria-expanded={showMd}
            >
              <FileText size={16} />
              {showMd ? 'Réduire le rapport' : 'Afficher le rapport (.md)'}
              {showMd ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          ) : null}
        </div>
      </div>

      <p className="text-xs text-neutral-400 mb-4">
        Mensualité TTC et contrats annexes extraits par le modèle — à vérifier sur votre document signé.
      </p>

      {loadState === 'loading' && !propMd ? (
        <div className="flex items-center gap-2 text-sm text-neutral-500 border-t border-neutral-100 pt-4">
          <Loader2 size={16} className="animate-spin text-green-600" />
          Chargement de la synthèse…
        </div>
      ) : null}

      {loadState === 'error' && !propMd ? (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 p-3 border-t-0 rounded-none" data-testid="contract-digest-error">
          La synthèse n&apos;est pas disponible (fichier introuvable ou OpenAI non configuré côté serveur). Utilisez « Réessayer » ou vérifiez que{' '}
          <code className="text-xs bg-amber-100 px-1">OPENAI_API_KEY</code> est défini dans le backend.
        </p>
      ) : null}

      {hasBody && showMd ? (
        <div className="max-h-[28rem] overflow-y-auto pr-1 border-t border-neutral-100 pt-4 markdown-digest">
          <ReactMarkdown components={mdComponents}>{markdown}</ReactMarkdown>
        </div>
      ) : null}

      {!hasBody && loadState === 'ok' && !propMd && !fetchedMd ? (
        <p className="text-sm text-neutral-500 border-t border-neutral-100 pt-4">
          Aucun contenu dans le fichier de synthèse.
        </p>
      ) : null}
    </div>
  );
}
